import { useState, useEffect } from 'react';
import { Search, X, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface SpecificRecipientPickerProps {
  targetTable: string;
  selectedEmails: string[];
  onEmailsChange: (emails: string[]) => void;
}

interface RecipientOption {
  email: string;
  name: string;
}

export default function SpecificRecipientPicker({
  targetTable,
  selectedEmails,
  onEmailsChange,
}: SpecificRecipientPickerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<RecipientOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      const timer = setTimeout(() => {
        searchRecipients();
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  }, [searchQuery, targetTable]);

  const searchRecipients = async () => {
    setLoading(true);
    try {
      let query;

      if (targetTable === 'game_dev_applications') {
        query = supabase
          .from('game_dev_applications')
          .select('email, full_name')
          .or(`email.ilike.%${searchQuery}%,full_name.ilike.%${searchQuery}%`)
          .limit(10);
      } else {
        query = supabase
          .from('email_list')
          .select('email, full_name')
          .eq('wants_marketing', true)
          .is('unsubscribed_at', null)
          .or(`email.ilike.%${searchQuery}%,full_name.ilike.%${searchQuery}%`)
          .limit(10);
      }

      const { data, error } = await query;

      if (!error && data) {
        const results = data
          .filter(item => !selectedEmails.includes(item.email))
          .map(item => ({
            email: item.email,
            name: item.full_name || 'No name',
          }));
        setSearchResults(results);
        setShowResults(true);
      }
    } catch (error) {
      console.error('Failed to search recipients:', error);
    } finally {
      setLoading(false);
    }
  };

  const addRecipient = (recipient: RecipientOption) => {
    if (!selectedEmails.includes(recipient.email)) {
      onEmailsChange([...selectedEmails, recipient.email]);
      setSearchQuery('');
      setShowResults(false);
    }
  };

  const removeRecipient = (email: string) => {
    onEmailsChange(selectedEmails.filter(e => e !== email));
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-bold text-gray-300 mb-2">
          Select Specific Recipients (Optional)
        </label>
        <div className="relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full pl-10 pr-4 py-2 bg-black border border-yellow-400/30 rounded-lg text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none"
            />
          </div>

          {showResults && (
            <div className="absolute z-10 w-full mt-1 bg-gray-900 border border-yellow-400/30 rounded-lg shadow-lg max-h-64 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-gray-400">
                  Searching...
                </div>
              ) : searchResults.length === 0 ? (
                <div className="p-4 text-center text-gray-400">
                  No recipients found
                </div>
              ) : (
                searchResults.map((recipient) => (
                  <button
                    key={recipient.email}
                    type="button"
                    onClick={() => addRecipient(recipient)}
                    className="w-full text-left px-4 py-3 hover:bg-yellow-400/10 transition-colors border-b border-yellow-400/20 last:border-b-0"
                  >
                    <p className="font-medium text-white">{recipient.name}</p>
                    <p className="text-sm text-gray-400">{recipient.email}</p>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {selectedEmails.length > 0 && (
        <div>
          <p className="text-sm font-bold text-gray-300 mb-2">
            Selected Recipients ({selectedEmails.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedEmails.map((email) => (
              <div
                key={email}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-yellow-400/20 text-yellow-400 border border-yellow-400/30 rounded-full text-sm"
              >
                <span>{email}</span>
                <button
                  type="button"
                  onClick={() => removeRecipient(email)}
                  className="hover:bg-yellow-400/30 rounded-full p-0.5 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedEmails.length === 1 && (
        <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-200">
            <p className="font-medium">Single recipient selected</p>
            <p className="mt-1">
              This campaign will only be sent to one person. Consider adding more recipients or adjusting your filters.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
